import { IShaderProgramInfo } from "../../../../SharedGL/Shader";
import { mat4 } from "gl-matrix";
import { Camera } from "../../../../SharedGL/Camera";
import { GroundEventShader } from "./GroundEventShader";

interface IFlightGLAttributes {
  position: WebGLBuffer;
  color: WebGLBuffer;
}

export class GroundEventBuffer {
  private shaderProgram: WebGLProgram;
  private programInfo: IShaderProgramInfo;

  protected buffers: IFlightGLAttributes;

  private positions: number[];
  private colors: number[];

  private texture: WebGLTexture = null;

  constructor(gl: WebGLRenderingContext, groundEventShader: GroundEventShader, texture: WebGLTexture) {
    // if (!GroundEventShader.texture) {
    this.texture = texture;
    // }

    this.shaderProgram = groundEventShader.shaderProgram;
    // Collect all the info needed to use the shader program.
    // Look up which attribute our shader program is using
    // for aVertexPosition and look up uniform locations.
    this.programInfo = {
      program: this.shaderProgram,
      attribLocations: {
        vertexPosition: gl.getAttribLocation(this.shaderProgram, "aVertexPosition"),
        vertexColor: gl.getAttribLocation(this.shaderProgram, "aVertexColor"),
      },
      uniformLocations: {
        projectionMatrix: gl.getUniformLocation(this.shaderProgram, "uProjectionMatrix"),
        modelViewMatrix: gl.getUniformLocation(this.shaderProgram, "uModelViewMatrix"),
        yPos: gl.getUniformLocation(this.shaderProgram, "uYPos"),
        uSampler: gl.getUniformLocation(this.shaderProgram, "uSampler"),
        uScale: gl.getUniformLocation(this.shaderProgram, "uScale"),
      },
    };
    // Here's where we call the routine that builds all the
    // objects we'll be drawing.
    this.buffers = this.initBuffers(gl);
  }

  //
  // initBuffers
  //
  // Initialize the buffers we'll need. For this demo, we just
  // have one object -- a simple two-dimensional square.
  //

  //CRAZY IDEA- buffer passes x0, x1 and x1, x0 that way vertex shader can compute the width of the object!!
  protected initBuffers = (gl: WebGLRenderingContext): IFlightGLAttributes => {
    // Create a buffer for the square's positions.

    const positionBuffer = gl.createBuffer();

    // Select the positionBuffer as the one to apply buffer
    // operations to from here out.

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);

    // Now create an array of positions for the square.
    this.positions = [];

    // Now pass the list of positions into WebGL to build the
    // shape. We do this by creating a Float32Array from the
    // JavaScript array, then use it to fill the current buffer.

    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.positions), gl.DYNAMIC_DRAW);

    this.colors = [];

    const colorBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.colors), gl.DYNAMIC_DRAW);

    return {
      position: positionBuffer,
      color: colorBuffer,
    };
  };

  public updateBuffers = (gl: WebGLRenderingContext, positions: number[], colors: number[]): void => {
    const positionBuffer = this.buffers.position;
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.DYNAMIC_DRAW);

    const colorBuffer = this.buffers.color;
    gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(colors), gl.DYNAMIC_DRAW);
  };

  private bindPositionsBuffer = (gl: WebGLRenderingContext): void => {
    // Tell WebGL how to pull out the positions from the position
    // buffer into the vertexPosition attribute.
    const numComponents = 2;
    const type = gl.FLOAT;
    const normalize = false;
    const stride = 0;
    const offset = 0;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position);
    gl.vertexAttribPointer(
      this.programInfo.attribLocations.vertexPosition,
      numComponents,
      type,
      normalize,
      stride,
      offset
    );
    gl.enableVertexAttribArray(this.programInfo.attribLocations.vertexPosition);
  };

  private bindColorBuffer = (gl: WebGLRenderingContext): void => {
    // Tell WebGL how to pull out the colors from the color buffer
    // into the vertexColor attribute.
    const numComponents = 4;
    const type = gl.FLOAT;
    const normalize = false;
    const stride = 0;
    const offset = 0;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.color);
    gl.vertexAttribPointer(
      this.programInfo.attribLocations.vertexColor,
      numComponents,
      type,
      normalize,
      stride,
      offset
    );
    gl.enableVertexAttribArray(this.programInfo.attribLocations.vertexColor);
  };

  public getNumVerts = (): number => {
    return this.positions.length / 2;
  };

  public draw = (
    gl: WebGLRenderingContext,
    camera: Camera,
    yPos: number,
    offset: number,
    numVerts: number,
    scale: number[]
  ): void => {
    const projectionMatrix = camera.getProjectionMatrix(gl);

    // Set the drawing position to the "identity" point, which is
    // the center of the scene.
    const modelViewMatrix = mat4.create();

    // Now move the drawing position a bit to where we want to
    // start drawing the square.

    mat4.translate(
      modelViewMatrix, // destination matrix
      modelViewMatrix, // matrix to translate
      [0.0, 0.0, -5.0] //set this to be drawn further back than flights
    ); // amount to translate

    // Tell WebGL to use our program when drawing
    gl.useProgram(this.programInfo.program);

    this.bindPositionsBuffer(gl);
    this.bindColorBuffer(gl);

    // Set the shader uniforms
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.projectionMatrix, false, projectionMatrix);
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.modelViewMatrix, false, modelViewMatrix);

    // Tell WebGL we want to affect texture unit 0
    gl.activeTexture(gl.TEXTURE0);

    // Bind the texture to texture unit 0
    gl.bindTexture(gl.TEXTURE_2D, this.texture);

    // Tell the shader we bound the texture to texture unit 0
    gl.uniform1i(this.programInfo.uniformLocations.uSampler, 0);
    gl.uniform2fv(this.programInfo.uniformLocations.uScale, scale);

    gl.uniform1f(this.programInfo.uniformLocations.yPos, yPos);
    gl.drawArrays(gl.TRIANGLES, offset, numVerts);
  };
}
